import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/redux'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { getDashboardNotificationById } from '@/services/adminDashboardService'
import { formatDateTimeSafe } from '@/pages/Dashboard/helpers/dateRange'
import type { DashboardNotificationLogDetailResponse } from '@/types/adminDashboard'

export interface NotificationDetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  notificationId: string | null
  token: string | null
}

export function NotificationDetailModal({
  open,
  onOpenChange,
  notificationId,
  token
}: NotificationDetailModalProps) {
  const dispatch = useDispatch<AppDispatch>()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<DashboardNotificationLogDetailResponse | null>(null)

  useEffect(() => {
    if (!open || !notificationId || !token) {
      setData(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setData(null)
    dispatch(getDashboardNotificationById(token, notificationId))
      .then((resp) => {
        if (!cancelled && resp.data) setData(resp.data)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, notificationId, token, dispatch])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto text-card-foreground">
        <DialogHeader>
          <DialogTitle>Notification details</DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-card-foreground/85">Loading…</p>}
        {!loading && data && (
          <div className="space-y-4">
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="font-medium text-card-foreground/85">Event type</dt>
                <dd>{data.eventTypeCode}</dd>
              </div>
              <div>
                <dt className="font-medium text-card-foreground/85">Channel</dt>
                <dd>{data.channel}</dd>
              </div>
              <div>
                <dt className="font-medium text-card-foreground/85">Recipient</dt>
                <dd>{data.recipient}</dd>
              </div>
              <div>
                <dt className="font-medium text-card-foreground/85">Status</dt>
                <dd>{data.status}</dd>
              </div>
              <div>
                <dt className="font-medium text-card-foreground/85">Created</dt>
                <dd>{formatDateTimeSafe(data.dateCreated)}</dd>
              </div>
              {data.dateSent != null && data.dateSent !== '' && (
                <div>
                  <dt className="font-medium text-card-foreground/85">Sent</dt>
                  <dd>{formatDateTimeSafe(data.dateSent)}</dd>
                </div>
              )}
              {data.organizationName && (
                <div>
                  <dt className="font-medium text-card-foreground/85">Organization</dt>
                  <dd>{data.organizationName}</dd>
                </div>
              )}
              {data.subject && (
                <div>
                  <dt className="font-medium text-card-foreground/85">Subject</dt>
                  <dd>{data.subject}</dd>
                </div>
              )}
              {data.errorMessage && (
                <div>
                  <dt className="font-medium text-card-foreground/85">Error</dt>
                  <dd className="text-destructive">{data.errorMessage}</dd>
                </div>
              )}
            </dl>
            {data.content != null && data.content !== '' && (
              <div>
                <h4 className="mb-2 text-sm font-medium">Content</h4>
                <pre className="max-h-60 overflow-auto rounded-md border bg-muted/30 p-3 text-xs whitespace-pre-wrap break-words">
                  {data.content}
                </pre>
              </div>
            )}
            {data.metadata != null && Object.keys(data.metadata).length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-medium">Metadata</h4>
                <pre className="max-h-40 overflow-auto rounded-md border bg-muted/30 p-3 text-xs">
                  {JSON.stringify(data.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
        {!loading && !data && open && notificationId && (
          <p className="text-sm text-card-foreground/85">No details available.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}
